import {Badge, Dropdown} from 'react-bootstrap'
import moment from 'moment'
import {IKanbanTask} from '../../../types/kanban'

const SEMAPHORE_LIMITS = {
  red: 5,
  green: 3,
}

const getTaskTraffic = (task: Pick<IKanbanTask, 'dueDate' | 'createdAt'>) => {
  const now = new Date()
  const dayMs = 1000 * 60 * 60 * 24

  if (task.dueDate) {
    const due = new Date(task.dueDate)
    const diffDays = Math.floor((due.getTime() - now.getTime()) / dayMs)

    if (diffDays < 0) {
      return {
        className: 'traffic-red',
        title: `Task overdue by ${Math.abs(diffDays)} day(s)`,
      }
    }
    if (diffDays <= SEMAPHORE_LIMITS.green) {
      return {
        className: 'traffic-orange',
        title: `Due in ${diffDays} day(s)`,
      }
    }
    return {
      className: 'traffic-green',
      title: `Due in ${diffDays} day(s)`,
    }
  }

  if (task.createdAt) {
    const created = new Date(task.createdAt)
    const ageDays = Math.floor((now.getTime() - created.getTime()) / dayMs)

    if (ageDays >= SEMAPHORE_LIMITS.red) {
      return {
        className: 'traffic-red',
        title: `Created ${ageDays} day(s) ago`,
      }
    }
    if (ageDays >= SEMAPHORE_LIMITS.green) {
      return {
        className: 'traffic-orange',
        title: `Created ${ageDays} day(s) ago`,
      }
    }
    return {
      className: 'traffic-green',
      title: `Created ${ageDays} day(s) ago`,
    }
  }

  return {
    className: 'traffic-green',
    title: '',
  }
}

interface Props {
  task: IKanbanTask
  onOpenAttachments?: (task: IKanbanTask) => void
  onEditTask?: (task: IKanbanTask) => void
  onAddComment?: (task: IKanbanTask) => void
  isClosedStage?: boolean
  currentUserEmail?: string
  onArchiveTask?: (task: IKanbanTask) => void
  onDeleteTask?: (task: IKanbanTask) => void
}

export const KanbanCard: React.FC<Props> = ({
  task,
  onOpenAttachments,
  onEditTask,
  onAddComment,
  isClosedStage = false,
  currentUserEmail,
  onArchiveTask,
  onDeleteTask,
}) => {
  const dueLabel = task.dueDate ? moment(task.dueDate).format('MMM DD') : null
  const normalizedCurrentEmail = currentUserEmail?.toLowerCase() ?? ''
  const isCurrentUserAssignee =
    !!normalizedCurrentEmail &&
    !!task.assignee?.email &&
    task.assignee.email.toLowerCase() === normalizedCurrentEmail
  const assigneeName = task.assignee
    ? isCurrentUserAssignee
      ? ''
      : `${task.assignee.firstName} ${task.assignee.lastName}`
    : 'Sin asignar'
  const groupLabel = task.assignmentGroup?.name ?? ''
  const completedChecklistCount =
    task.checklistItems?.filter((item) => item.isCompleted).length ?? 0
  const totalChecklistCount = task.checklistItems?.length ?? 0
  const participantEntry = normalizedCurrentEmail
    ? task.participants?.find((participant) =>
        participant.user?.email
          ? participant.user.email.toLowerCase() === normalizedCurrentEmail
          : false
      )
    : undefined
  const isCreatorCurrentUser =
    !!normalizedCurrentEmail &&
    !!task.creator?.email &&
    task.creator.email.toLowerCase() === normalizedCurrentEmail
  const isParticipantOnly =
    !!participantEntry && participantEntry.role !== 'OWNER' && !isCreatorCurrentUser
  const traffic = getTaskTraffic({dueDate: task.dueDate, createdAt: task.createdAt})
  const renderPriorityBadge = () => {
    if (task.priority === 'NORMAL') return null
    if (task.priority === 'URGENT') {
      return (
        <span className='kanban-card__priority kanban-card__priority--urgent'>
          <i className='bi bi-exclamation-octagon-fill me-1' />
          Urgente
        </span>
      )
    }
    if (task.priority === 'LOW') {
      return (
        <span className='kanban-card__priority kanban-card__priority--low'>
          <i className='bi bi-info-circle-fill me-1' />
          Baja
        </span>
      )
    }
    return (
      <span className='kanban-card__priority kanban-card__priority--high'>
        <i className='bi bi-exclamation-triangle-fill me-1' />
        Alta
      </span>
    )
  }
  const priorityBadge = renderPriorityBadge()
  const handleDoubleClick = () => {
    onEditTask?.(task)
  }

  return (
    <div className='kanban-card' data-task-id={task.id} onDoubleClick={handleDoubleClick}>
      <div
        className='kanban-card__header task'
        style={{
          borderTop: task.tagColor ? `2px solid ${task.tagColor}` : undefined,
        }}
      >
        <div className='kanban-card__title'>
          {isClosedStage && <i className='bi bi-check-circle-fill text-success me-2' />}
          {task.title}
        </div>
        {(onEditTask || onOpenAttachments || onAddComment || onArchiveTask || onDeleteTask) && (
          <Dropdown align='end' className='menu-dropdown dropdown menu-small' drop='down'>
            <Dropdown.Toggle variant='link' size='sm' className='p-0 text-dark'>
              <i className='bi bi-three-dots-vertical' />
            </Dropdown.Toggle>
            <Dropdown.Menu
              className='dropdown-menu-task'
              popperConfig={{
                strategy: 'fixed',
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                  {
                    name: 'computeStyles',
                    options: {
                      gpuAcceleration: false,
                    },
                  },
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
              }}
              renderOnMount
            >
              {onEditTask && (
                <Dropdown.Item onClick={() => onEditTask(task)}>
                  <i className='bi bi-pencil fs-3' style={{color: 'blue'}} />
                  Edit task
                </Dropdown.Item>
              )}
              {onOpenAttachments && (
                <Dropdown.Item onClick={() => onOpenAttachments(task)}>
                  <i className='bi bi-upload' />
                  Upload File
                </Dropdown.Item>
              )}
              {onAddComment && (
                <Dropdown.Item onClick={() => onAddComment(task)}>
                  <i className='bi bi-plus-circle' />
                  Add Comment
                </Dropdown.Item>
              )}
              {onArchiveTask && (
                <Dropdown.Item onClick={() => onArchiveTask(task)}>
                  <i className='bi bi-folder-fill fs-3' style={{color: 'inherit'}} /> Archived
                </Dropdown.Item>
              )}
              {onDeleteTask && (
                <Dropdown.Item className='text-danger' onClick={() => onDeleteTask(task)}>
                  <i className='bi bi-trash' />
                  Deletee
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      {!isClosedStage && (
        <div className={traffic.className} title={traffic.title} style={{width: '100%'}} />
      )}

      <div className='kanban-card__meta'>
        <div className='kanban-card__assignee'>
          {isParticipantOnly && (
            <span className='kanban-card__participant-indicator' title='Participas en esta tarea'>
              <i title='@Me' className='bi bi-person-plus-fill' />
            </span>
          )}
          {assigneeName}
        </div>
        {task.labelText && (
          <div className='kanban-card__label badge bg-light text-dark'>{task.labelText}</div>
        )}
      </div>
      {(groupLabel || priorityBadge || totalChecklistCount > 0 || dueLabel) && (
        <div className='kanban-card__footer'>
          {dueLabel && (
            <Badge bg='secondary' className='kanban-card__due'>
              {dueLabel}
            </Badge>
          )}
          {groupLabel && <span className='kanban-card__group'>{groupLabel}</span>}
          {priorityBadge}
          {totalChecklistCount > 0 && (
            <span className='kanban-card__checklist-progress'>
              <i className='bi bi-check-square me-1' />
              {completedChecklistCount}/{totalChecklistCount}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
